import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { Subscription } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useState, useEffect } from 'react';

export const Active = () => {
  const { showToastMessage } = useToast();

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`p-2 ${loading ? 'opacity-50' : 'opacity-100'}`}>
      {/* First Card - about plan */}
      <div className="border border-[#E8E8E8] p-3 rounded-md">
        <div className="flex flex-row justify-between">
          <div className="flex-1">
            <span className="text-2xl text-[#292D32] font-bold uppercase">
              {subscriptionDetails?.subscriptionPlan}
            </span>
            <div className="inline-block mb-1 ml-2">
              <div className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-[#292D321A] text-[#292D32] w-fit">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 5C0 2.23858 2.23858 0 5 0C7.76142 0 10 2.23858 10 5C10 7.76142 7.76142 10 5 10C2.23858 10 0 7.76142 0 5Z"
                    fill="#10B981"
                  />
                </svg>
                CURRENT PLAN
              </div>
            </div>
          </div>
          <div className="flex">
            <div className="mt-1">
              <span className="font-bold text-2xl">$</span>
              <span className="text-4xl font-bold">
                {subscriptionDetails?.price}{' '}
              </span>
              <span className="mb-3 text-[#292D32] font-medium"> /Animal</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <span className="mt-3 font-medium text-[#292D32]">
            You&apos;re currently on the Professional plan with advanced
            features and priority support. Your plan renews automatically each
            month.
          </span>

          <span className="mt-3 font-bold uppercase">
            Member since{' '}
            {handleDateFormat(subscriptionDetails?.subscriptionStartDate)}
          </span>
        </div>
      </div>
    </div>
  );
};
